import React from "react";
import { useTranslation } from "react-i18next";
import {
  Control,
  FieldPath,
  FieldValues,
  useController,
  UseControllerProps,
} from "react-hook-form";
import { resolveError } from "@/validation/messages";
import { FormField } from "./FormField";
import { FormInput, FormInputProps } from "./FormInput";
import { FormPasswordInput } from "./FormPasswordInput";

type InputOnlyProps = Omit<
  FormInputProps,
  "value" | "onChangeText" | "onBlur" | "error"
>;

type Props<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = InputOnlyProps & {
  control: Control<TFieldValues>;
  name: TName;
  rules?: UseControllerProps<TFieldValues, TName>["rules"];
  label?: string;
  password?: boolean;
};

export function RHFFormInput<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  rules,
  label,
  password,
  ...inputProps
}: Props<TFieldValues, TName>) {
  const { t } = useTranslation();
  const { field, fieldState } = useController<TFieldValues, TName>({
    control,
    name,
    rules,
  });

  const errorMessage = resolveError(fieldState.error, t);

  const Input = password ? FormPasswordInput : FormInput;

  return (
    <FormField label={label} error={errorMessage}>
      <Input
        value={(field.value ?? "") as string}
        onChangeText={field.onChange}
        onBlur={field.onBlur}
        {...inputProps}
      />
    </FormField>
  );
}

export default RHFFormInput;
